import * as React from "react";
import { weekNumber, getDateRangeStrForWeek, getDateOfISOWeek, weekYear } from "@/lib/util";
import { translate } from "../IntlProviderWrapper";
import Popup from "./Popup";

type ISelectOption = {
    value: number;
    label: number;
};

type WeekSelectProps = {
    onClickWeek: (option: number) => (e: React.MouseEvent<HTMLDivElement>) => void;
    selected_week: number | null;
    selected_year: number | null;
};
type WeekSelectState = {
    options: ISelectOption[];
};

class WeekSelect extends React.Component<WeekSelectProps, WeekSelectState> {
    state: WeekSelectState = {
        options: [],
    };

    componentDidMount() {
        this.load();
    }

    componentDidUpdate(prev_props: WeekSelectProps) {
        if (this.props.selected_week && prev_props.selected_week === this.props.selected_week) {
            const el = document.querySelector("#week-" + this.props.selected_week);
            if (el) {
                el.scrollIntoView();
            }
        }
    }

    load() {
        const weeks: ISelectOption[] = [];
        for (let counter = 1; counter < 54; counter++) {
            weeks.push({ label: counter, value: counter });
        }

        this.setState({ options: weeks });
    }

    renderOption = (option: ISelectOption) => {
        const selected_year = this.props.selected_year;
        if (!selected_year) return null;

        const selectedClassName = this.props.selected_week === option.value ? "selected" : "";
        const className = "custom-select-item " + selectedClassName;
        const date_range = getDateRangeStrForWeek(option.value, selected_year);
        if (!date_range.includes(selected_year + "")) return null;

        return (
            <div
                key={option.value}
                id={`week-${option.value}`}
                className={className}
                title={date_range}
                onClick={this.props.onClickWeek(option.value)}
            >
                {option.label}
            </div>
        );
    };

    render() {
        return <div className="custom-select-menu-week">{this.state.options.map(this.renderOption)}</div>;
    }
}

type YearSelectProps = {
    onClickYear: (option: number) => (e: React.MouseEvent<HTMLDivElement>) => void;
    selected_year: number | null;
};
type YearSelectState = {
    options: ISelectOption[];
};

class YearSelect extends React.Component<YearSelectProps, YearSelectState> {
    state: YearSelectState = {
        options: [],
    };

    componentDidMount() {
        this.load();
    }

    load() {
        const current_year = new Date().getFullYear();
        const options = [
            {
                value: current_year - 1,
                label: current_year - 1,
            },
            {
                value: current_year,
                label: current_year,
            },
            {
                value: current_year + 1,
                label: current_year + 1,
            },
        ];
        this.setState({ options });
    }

    renderOption = (option: ISelectOption) => {
        const selectedClassName = this.props.selected_year === option.value ? "selected" : "";
        const className = "custom-select-item " + selectedClassName;
        return (
            <div key={option.value} className={className} onClick={this.props.onClickYear(option.value)}>
                {option.label}
            </div>
        );
    };

    render() {
        return <div className="custom-select-menu-year">{this.state.options.map(this.renderOption)}</div>;
    }
}

type Props = {
    onWeekPickerChange: (week: number, year: number) => void;
    filter_week: number;
    filter_year: number;
};
type State = {
    selected_year: number | null;
    selected_week: number | null;
    show_week_picker: boolean;
};

export default class WeekPicker extends React.Component<Props, State> {
    click_listener: ((e: Event) => void) | null = null;
    trigger_ref: { current: HTMLButtonElement | null } = React.createRef();

    state: State = {
        show_week_picker: false,
        selected_year: null,
        selected_week: null,
    };

    componentDidMount() {
        this.click_listener = (e: Event) => this.onWindowClick(e);
        window.addEventListener("click", this.click_listener);
        // state initialized from the ... OUTSIDE
        if (this.props.filter_week && this.props.filter_year) {
            this.setState({
                selected_week: this.props.filter_week,
                selected_year: this.props.filter_year,
            });
        }
    }

    componentWilUnmount() {
        if (this.click_listener) {
            window.removeEventListener("click", this.click_listener);
        }
    }

    onWindowClick = (e: Event) => {
        const current_target = e.target;
        if (current_target instanceof HTMLElement) {
            const should_close = !current_target.closest("[id^=custom-week-picker]");
            if (should_close) {
                this.setState({ show_week_picker: false });
            }
        }
    };

    onClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        this.setState({ show_week_picker: true });
    };

    onClose = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        this.setState({ show_week_picker: false });
    };

    onSave = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        if (this.state.selected_week && this.state.selected_year) {
            this.props.onWeekPickerChange(this.state.selected_week, this.state.selected_year);
        }
        this.setState({ show_week_picker: false });
    };

    onClickYear = (selected_year: number) => () => {
        this.setState({ selected_year });
    };

    onClickWeek = (selected_week: number) => () => {
        this.setState({ selected_week });
    };

    onClickLeft = () => {
        if (this.state.selected_week && this.state.selected_year) {
            const date = getDateOfISOWeek(this.state.selected_week - 1, this.state.selected_year);
            const week = weekNumber(date);
            const year = weekYear(date);

            this.setState({
                selected_week: week,
                selected_year: year,
            });
            this.props.onWeekPickerChange(week, year);
        }
    };

    onClickRight = () => {
        if (this.state.selected_week && this.state.selected_year) {
            const date = getDateOfISOWeek(this.state.selected_week + 1, this.state.selected_year);
            const week = weekNumber(date);
            const year = weekYear(date);

            this.setState({
                selected_week: week,
                selected_year: year,
            });
            this.props.onWeekPickerChange(week, year);
        }
    };

    render() {
        const trigger_el = this.trigger_ref.current;
        return (
            <div className="custom-week-picker-tooltip-container">
                {this.state.show_week_picker && trigger_el !== null && (
                    <Popup target={trigger_el} alignment={"center"} isOpen={this.state.show_week_picker}>
                        <div id="custom-week-picker-tooltip" className="custom-week-picker-tooltip">
                            <div className="custom-week-picker-tooltip-border">
                                <div className="week-select">
                                    <WeekSelect
                                        onClickWeek={this.onClickWeek}
                                        selected_week={this.state.selected_week}
                                        selected_year={this.state.selected_year}
                                    />
                                </div>
                                <div className="year-select">
                                    <YearSelect
                                        onClickYear={this.onClickYear}
                                        selected_year={this.state.selected_year}
                                    />
                                </div>
                            </div>
                            <div>
                                <button
                                    className="btn btn-primary"
                                    style={{ position: "relative", width: "200px", height: "30px" }}
                                    onClick={this.onSave}
                                >
                                    {translate("common.save", "Save")}
                                </button>
                            </div>
                        </div>
                    </Popup>
                )}
                <div className="d-flex">
                    <button className="btn btn-primary decrement" onClick={this.onClickLeft}>
                        <i className="fas fa-caret-left"></i>
                    </button>
                    <button className="btn btn-primary" onClick={this.onClick} ref={this.trigger_ref}>
                        {this.state.selected_week} / {this.state.selected_year}
                    </button>
                    <button className="btn btn-primary increment" onClick={this.onClickRight}>
                        <i className="fas fa-caret-right"></i>
                    </button>
                </div>
            </div>
        );
    }
}
